import { T, useGT, Var } from 'gt-react';
import type { TaskTypeResponse } from '@erp/rpc';
import { MetricCard } from '@/components/accounts-receivable/MetricCard';
import { useTaskTypeMetrics } from 'src/api/rpc/hooks/tasks/useTaskTypes';
import { TaskAgingCard } from './TaskAgingCard';
import { anchorMetricFieldOptions } from './anchorMetricFields';
import {
  taskMetricCardViews,
  taskMetricGridClass,
  type TaskMetricCardView,
} from './taskMetricCardViews';

type ValueCardView = Exclude<TaskMetricCardView, { kind: 'aging' }>;

function DefaultValueTitle({ card }: { card: ValueCardView }) {
  switch (card.kind) {
    case 'open_count':
      return (
        <T context="task page metric card title, count of open tasks">Open</T>
      );
    case 'unassigned':
      return (
        <T context="task page metric card title, open tasks with no assignee">
          Unassigned
        </T>
      );
    case 'sum':
      return (
        <T context="task page metric card title, total of a numeric task field across open tasks">
          Total <Var>{card.fieldLabel}</Var>
        </T>
      );
  }
}

/**
 * The metric cards a task type configured (open count, unassigned, aging,
 * field sums), above that type's queue on a Task Page. Counts cover the open
 * tasks the viewer can see. Renders nothing when the type shows no cards.
 */
export function TaskMetricCards({
  taskTypeId,
  taskType,
  excludeSnoozed = false,
}: {
  taskTypeId: string;
  /** The type's config: placeholders while loading, sum field formats. */
  taskType: TaskTypeResponse | undefined;
  /** Count like a queue that hides snoozed tasks. */
  excludeSnoozed?: boolean;
}) {
  const gt = useGT();
  // Cards are opt-in: a type with none configured costs no request.
  const hasCards = (taskType?.metrics?.length ?? 0) > 0;
  const { data, isError } = useTaskTypeMetrics(
    taskTypeId,
    excludeSnoozed,
    hasCards,
  );
  // Cached values from before the cards were removed must not linger.
  if (!hasCards) return null;
  const cards = taskMetricCardViews({
    loaded: data?.cards,
    configured: taskType?.metrics,
    inputFields: [
      ...(taskType?.inputSchema?.fields ?? []),
      ...anchorMetricFieldOptions(taskType?.anchorEntityType, gt),
    ],
  });
  if (isError || cards.length === 0) return null;

  return (
    <div className={taskMetricGridClass(cards.length)}>
      {cards.map((card) =>
        card.kind === 'aging' ? (
          <TaskAgingCard
            key={card.key}
            aging={card.buckets}
            title={card.label ?? undefined}
          />
        ) : (
          <MetricCard
            key={card.key}
            label={card.label ?? <DefaultValueTitle card={card} />}
            value={card.display}
            isLoading={!data}
          />
        ),
      )}
    </div>
  );
}
